import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const STATUS_LABELS = {
  draft: "Concept",
  sent: "Verzonden",
  accepted: "Geaccepteerd",
  rejected: "Afgewezen",
  invoiced: "Gefactureerd",
  paid: "Betaald",
};

const TYPE_LABELS = {
  quote: "Offerte",
  invoice: "Factuur",
};

function fmt(value) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(value || 0);
}

export default function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api
      .get(`/quotes/public/${token}`)
      .then((response) => setQuote(response.data))
      .catch(() => setMessage("Deze offerte kon niet worden gevonden."));
  }, [token]);

  const respond = async (action) => {
    setBusy(action);
    setMessage("");
    try {
      const { data } = await api.post(`/quotes/public/${token}/${action}`);
      setQuote(data);
      setMessage(action === "accept" ? "Bedankt, de offerte is geaccepteerd." : "De offerte is afgewezen.");
    } catch (error) {
      setMessage(error.response?.data?.detail || "Reactie opslaan is mislukt.");
    } finally {
      setBusy("");
    }
  };

  if (!quote && !message) {
    return <div className="min-h-screen bg-background text-foreground p-8">Offerte laden...</div>;
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center p-6">
        <div className="max-w-lg rounded-xl border border-border bg-card p-8 text-center">
          <h1 className="font-display text-3xl mb-3">Offerte niet gevonden</h1>
          <p className="text-muted-foreground">{message}</p>
        </div>
      </div>
    );
  }

  const canRespond = !["accepted", "rejected", "paid"].includes(quote.status);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="mx-auto max-w-4xl px-5 py-10">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="overline mb-2">Zippe International</div>
            <h1 className="font-display text-4xl leading-none">{TYPE_LABELS[quote.type] || "Document"} {quote.number}</h1>
            <p className="mt-2 text-muted-foreground">{quote.title}</p>
          </div>
          <Badge variant="outline" className="w-fit">{STATUS_LABELS[quote.status] || quote.status}</Badge>
        </div>

        <section className="rounded-xl border border-border bg-card p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Info label="Klant" value={quote.customer_name} />
            <Info label="Datum" value={quote.created_at ? new Date(quote.created_at).toLocaleDateString("nl-NL") : "-"} />
          </div>

          <div className="mt-8 overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr className="text-left">
                  <th className="px-4 py-3 overline">Omschrijving</th>
                  <th className="px-4 py-3 overline text-right">Aantal</th>
                  <th className="px-4 py-3 overline text-right">Prijs</th>
                  <th className="px-4 py-3 overline text-right">BTW</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {quote.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3">{item.description || "Regel"}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.quantity}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmt(item.unit_price)}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.tax_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 ml-auto max-w-sm space-y-2">
            <Total label="Subtotaal" value={fmt(quote.subtotal)} />
            <Total label="BTW" value={fmt(quote.tax_total)} />
            <Total label="Totaal" value={fmt(quote.total)} accent />
          </div>

          {quote.notes && (
            <div className="mt-6 rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
              {quote.notes}
            </div>
          )}
        </section>

        <section className="mt-6 rounded-xl border border-border bg-card p-6">
          {message && <div className="mb-4 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm">{message}</div>}
          {canRespond ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => respond("reject")} disabled={!!busy}>
                {busy === "reject" ? "Verwerken..." : "Weigeren"}
              </Button>
              <Button onClick={() => respond("accept")} disabled={!!busy}>
                {busy === "accept" ? "Verwerken..." : "Accepteren"}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Deze {TYPE_LABELS[quote.type].toLowerCase()} is {STATUS_LABELS[quote.status]?.toLowerCase() || quote.status}.
            </p>
          )}
        </section>
      </main>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="text-sm">{value || "-"}</div>
    </div>
  );
}

function Total({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono ${accent ? "text-lg text-primary" : ""}`}>{value}</span>
    </div>
  );
}
